import omit from 'lodash-es/omit'

import APPLICANTS_SNAPSHOT from '../../../../rental-portal-mock-data/applicants.json'
import EMAILS_SNAPSHOT from '../../../../rental-portal-mock-data/emails.json'
import {
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
} from '../../../constants/api'
import { EMPTY_LIST, EMPTY_STRING } from '../../../constants/common'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../../constants/rentalPortalMockState'
import { htmlToMultilineText } from '../../../utils/html'
import { toRentalPortalMockMediaUser } from '../../../utils/rentalPortalMockMedia'
import { hasRentalPortalMockState } from '../../../utils/rentalPortalMockState'
import { getIsRentalPortalApplicantIdentityMasked } from '../RentalPortalApplicantIdentityCell'

const MOCK_APPLICANT_EMAILS_READ_LATENCY = 300

const MOCK_APPLICANT_EMAILS_OVERFLOW_ROWS = 24

const MOCK_APPLICANT_EMAILS_READ_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_APPLICANT_EMAIL_MASKED_ERROR = {
  response: {
    data: { errors: 'ServerError.RentalApplicantEmailMaskedKey' },
    status: HTTP_STATUS_FORBIDDEN,
  },
}

const MOCK_APPLICANT_EMAIL_SEND_ERROR = {
  response: {
    data: { errors: 'ServerError.RentalApplicantEmailSendFailedKey' },
    status: HTTP_STATUS_INTERNAL_SERVER_ERROR,
  },
}

const MOCK_APPLICANT_EMAIL_DIRECTION_OUTBOUND = 'outbound'

const MOCK_APPLICANT_EMAIL_MASKED_BY_ID = new Map(
  APPLICANTS_SNAPSHOT.map(applicant => [
    applicant.id,
    getIsRentalPortalApplicantIdentityMasked(applicant),
  ])
)

function getIsMockApplicantEmailMasked(applicantId) {
  return MOCK_APPLICANT_EMAIL_MASKED_BY_ID.get(applicantId) !== false
}

function getMockApplicantEmailParticipant(email) {
  return email.direction === MOCK_APPLICANT_EMAIL_DIRECTION_OUTBOUND
    ? email.to?.[0]
    : email.from
}

const MOCK_APPLICANT_EMAIL_PARTICIPANT_PRIVATE_FIELDS = ['email']

function toServedMockApplicantEmailParticipant(participant) {
  return participant
    ? omit(
        toRentalPortalMockMediaUser(participant),
        MOCK_APPLICANT_EMAIL_PARTICIPANT_PRIVATE_FIELDS
      )
    : null
}

function toServedMockApplicantEmail(email) {
  const isMasked = getIsMockApplicantEmailMasked(email.applicant_id)

  return {
    has_attachments: email.has_attachments,
    id: email.id,
    is_read: email.is_read,
    participant: {
      masked: isMasked,
      value: isMasked
        ? null
        : toServedMockApplicantEmailParticipant(
            getMockApplicantEmailParticipant(email)
          ),
    },
    sent_at: email.sent_at,
    snippet: isMasked ? null : email.snippet,
    subject: isMasked ? null : email.subject,
    thread_id: email.thread_id,
  }
}

function toMockApplicantEmailsById(emails) {
  return [...emails]
    .sort((first, second) => second.sent_at.localeCompare(first.sent_at))
    .reduce((acc, email) => {
      const served = acc.get(email.applicant_id) ?? EMPTY_LIST

      return acc.set(email.applicant_id, [
        ...served,
        toServedMockApplicantEmail(email),
      ])
    }, new Map())
}

const MOCK_APPLICANT_EMAILS_BY_ID = toMockApplicantEmailsById(EMAILS_SNAPSHOT)

function toMockApplicantContactsById(emails) {
  return emails.reduce((acc, email) => {
    const participant = getMockApplicantEmailParticipant(email)

    return participant?.email && !acc.has(email.applicant_id)
      ? acc.set(email.applicant_id, participant)
      : acc
  }, new Map())
}

const MOCK_APPLICANT_CONTACT_BY_ID =
  toMockApplicantContactsById(EMAILS_SNAPSHOT)

export function getMockApplicantContact(applicantId) {
  return MOCK_APPLICANT_CONTACT_BY_ID.get(Number(applicantId)) ?? null
}

function getMockApplicantEmailsOverflowRows(emails) {
  return emails.length > 0
    ? Array.from(
        { length: MOCK_APPLICANT_EMAILS_OVERFLOW_ROWS },
        (_, index) => ({
          ...emails[index % emails.length],
          id: `${emails[index % emails.length].id}-${index}`,
        })
      )
    : emails
}

function getMockApplicantEmailsSource(applicantId) {
  const emails =
    MOCK_APPLICANT_EMAILS_BY_ID.get(Number(applicantId)) ?? EMPTY_LIST

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.bulk)
    ? getMockApplicantEmailsOverflowRows(emails)
    : emails
}

// TODO: `GET /api/v3/rental-portal/applicants/{id}/emails/` is unbuilt and needs the
// `Email` ↔ `Applicant` link first  — envelope inferred
export function getMockApplicantEmailCount(applicantId) {
  return getMockApplicantEmailsSource(applicantId).length
}

function withMockApplicantEmailsLatency() {
  const delay = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
    ? RENTAL_PORTAL_MOCK_LATENCY
    : MOCK_APPLICANT_EMAILS_READ_LATENCY

  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

export function getMockApplicantEmails({
  applicant_id: applicantId,
  page,
  page_size: pageSize,
}) {
  return withMockApplicantEmailsLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_APPLICANT_EMAILS_READ_ERROR)
    }

    const emails = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : getMockApplicantEmailsSource(applicantId)

    const pageCount = Math.ceil(emails.length / pageSize)
    const currentPage = Math.min(page, Math.max(pageCount, 1))
    const firstIndex = (currentPage - 1) * pageSize

    return {
      data: {
        pagination: {
          page: currentPage,
          page_count: pageCount,
          total: emails.length,
        },
        results: {
          objects: emails.slice(firstIndex, firstIndex + pageSize),
        },
      },
    }
  })
}

function toMockApplicantEmailSnippet(text) {
  return htmlToMultilineText(text ?? EMPTY_STRING)
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join(' ')
}

let nextMockApplicantEmailId = 9000

// TODO: `POST /api/rental-portal/applicant/{id}/email/` is unbuilt and needs the
// `Email` ↔ `Applicant` link first (RP-04 §10 row 11) — envelope inferred. `To` is
// resolved here from the applicant, never read from the client payload.
export function postMockApplicantEmail({
  applicantId,
  hasFiles,
  subject,
  text,
}) {
  return withMockApplicantEmailsLatency().then(() => {
    const id = Number(applicantId)

    if (getIsMockApplicantEmailMasked(id)) {
      return Promise.reject(MOCK_APPLICANT_EMAIL_MASKED_ERROR)
    }

    const contact = getMockApplicantContact(id)

    if (
      !contact ||
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)
    ) {
      return Promise.reject(MOCK_APPLICANT_EMAIL_SEND_ERROR)
    }

    nextMockApplicantEmailId += 1

    const sent = {
      applicant_id: id,
      direction: MOCK_APPLICANT_EMAIL_DIRECTION_OUTBOUND,
      has_attachments: Boolean(hasFiles),
      id: nextMockApplicantEmailId,
      is_read: true,
      sent_at: new Date().toISOString(),
      snippet: toMockApplicantEmailSnippet(text),
      subject,
      thread_id: `thr-a${id}-${nextMockApplicantEmailId}`,
      to: [contact],
    }

    MOCK_APPLICANT_EMAILS_BY_ID.set(id, [
      toServedMockApplicantEmail(sent),
      ...(MOCK_APPLICANT_EMAILS_BY_ID.get(id) ?? EMPTY_LIST),
    ])

    return { data: { id: sent.id, thread_id: sent.thread_id } }
  })
}
